import { useState, useEffect } from "react";
import { CheckCircle2, ShieldCheck, ArrowRight, AlertTriangle } from "lucide-react";
import QstLayout, { AiSafetyBadge } from "./QstLayout";
import { qstStorySubmit } from "./api";

export default function QstStoryPage() {
  const [form, setForm] = useState({
    contributor_name: "", email: "", trade: "",
    project_background: "", what_went_wrong: "",
    root_causes: "", corrective_actions: "", prevention_recommendations: "",
    discussion_questions: "",
    ownership_permission: false, anonymize: true,
    publication_preference: "review-then-decide", reward_preference: "editorial-review",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => { document.title = "Tell Your Story — Quality Safety Times"; }, []);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await qstStorySubmit(form);
      setResult(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not submit — please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (result) return (
    <QstLayout>
      <section className="max-w-3xl mx-auto px-6 py-16 text-center" data-testid="qst-story-submitted">
        <CheckCircle2 className="w-14 h-14 mx-auto text-[#059669]" />
        <h1 className="mt-5 text-3xl font-black text-[#0B2545]">Thank you for sharing.</h1>
        <p className="mt-3 text-slate-700 leading-relaxed">{result.next_step}</p>
        {result.flags && result.flags.length > 0 && (
          <div className="mt-6 rounded-lg bg-amber-50 border border-amber-200 p-4 text-left">
            <div className="inline-flex items-center gap-2 text-amber-900 font-bold text-sm">
              <AlertTriangle className="w-4 h-4" /> Review flags detected
            </div>
            <ul className="mt-2 space-y-1 text-sm text-amber-900">
              {result.flags.map((f) => <li key={f}>• {f.replace(/-/g, ' ')}</li>)}
            </ul>
            <p className="mt-2 text-xs text-amber-800">Our editors will contact you before anything is published to confirm anonymization preferences.</p>
          </div>
        )}
      </section>
    </QstLayout>
  );

  return (
    <QstLayout>
      <section className="bg-slate-50 py-14 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold">Tell Your Story</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-black text-[#0B2545]">Share a lesson learned. Help the whole trade.</h1>
          <p className="mt-3 text-lg text-slate-700 max-w-2xl">
            Anonymized stories from experienced construction workers become the backbone of QST's lesson library.
            Every submission enters editorial review — nothing is published automatically.
          </p>
          <div className="mt-5"><AiSafetyBadge>Submitted stories are reviewed by an experienced construction editor and never published without your approval.</AiSafetyBadge></div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-10">
        <form onSubmit={submit} className="space-y-5" data-testid="qst-story-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Your name" required value={form.contributor_name} onChange={(v) => setForm({ ...form, contributor_name: v })} testid="qst-story-name" />
            <Field label="Email" required type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="qst-story-email" />
          </div>
          <Field label="Trade" required value={form.trade} onChange={(v) => setForm({ ...form, trade: v })} placeholder="e.g. Electrical — Commercial" testid="qst-story-trade" />
          <TextArea label="Project background" required rows={3} value={form.project_background} onChange={(v) => setForm({ ...form, project_background: v })} placeholder="Type of project, phase, scope — no company / project names please" testid="qst-story-bg" />
          <TextArea label="What went wrong" required rows={4} value={form.what_went_wrong} onChange={(v) => setForm({ ...form, what_went_wrong: v })} placeholder="Describe the incident, near-miss or defect discovered." testid="qst-story-wrong" />
          <TextArea label="Root causes"           rows={3} value={form.root_causes} onChange={(v) => setForm({ ...form, root_causes: v })} testid="qst-story-causes" />
          <TextArea label="Corrective actions taken" rows={3} value={form.corrective_actions} onChange={(v) => setForm({ ...form, corrective_actions: v })} testid="qst-story-corr" />
          <TextArea label="Prevention recommendations" rows={3} value={form.prevention_recommendations} onChange={(v) => setForm({ ...form, prevention_recommendations: v })} testid="qst-story-prev" />
          <TextArea label="Suggested discussion questions" rows={2} value={form.discussion_questions} onChange={(v) => setForm({ ...form, discussion_questions: v })} testid="qst-story-disc" />

          <div className="space-y-3 rounded-lg bg-slate-50 border border-slate-200 p-4">
            <label className="flex items-start gap-2.5 text-sm">
              <input type="checkbox" checked={form.anonymize} onChange={(e) => setForm({ ...form, anonymize: e.target.checked })} className="mt-0.5" data-testid="qst-story-anon" />
              <span className="text-slate-800">Anonymize my story before editorial review (recommended).</span>
            </label>
            <label className="flex items-start gap-2.5 text-sm">
              <input type="checkbox" required checked={form.ownership_permission} onChange={(e) => setForm({ ...form, ownership_permission: e.target.checked })} className="mt-0.5" data-testid="qst-story-permission" />
              <span className="text-slate-800">I own or have permission to share this content. QST may use it for educational purposes with proper attribution or full anonymization per my preference.</span>
            </label>
          </div>

          {err && <div className="text-sm text-red-700">{err}</div>}
          <button type="submit" disabled={busy || !form.ownership_permission}
            className="inline-flex items-center gap-2 rounded-md bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-50 text-white px-6 py-3 font-bold shadow-sm"
            data-testid="qst-story-submit">
            {busy ? "Submitting…" : <>Submit to editorial review <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </section>
    </QstLayout>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <label className="text-sm block">
      <div className="text-slate-700 font-semibold">{label}{required && <span className="text-red-600">*</span>}</div>
      <input type={type} required={required} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#0B2545] focus:ring-2 focus:ring-[#0B2545]/10 outline-none"
        data-testid={testid} />
    </label>
  );
}
function TextArea({ label, value, onChange, rows, required, testid, placeholder }) {
  return (
    <label className="text-sm block">
      <div className="text-slate-700 font-semibold">{label}{required && <span className="text-red-600">*</span>}</div>
      <textarea rows={rows || 3} required={required} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#0B2545] focus:ring-2 focus:ring-[#0B2545]/10 outline-none"
        data-testid={testid} />
    </label>
  );
}
